import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp } from "lucide-react";

const PERIODS = [
  { label: "30 jours", days: 30 },
  { label: "90 jours", days: 90 },
  { label: "12 mois", days: 365 },
  { label: "Tout", days: 0 },
];

const PAGE_LABELS = {
  "/evenements": "Événements (hub)",
  "/mariage": "Mariage",
  "/seminaire": "Séminaire",
  "/bapteme": "Baptême & communion",
  "/anniversaire": "Anniversaire",
  "—": "Non renseigné",
};

const Bars = ({ rows, total, labels, testid }) => (
  <div className="space-y-3" data-testid={testid}>
    {rows.length === 0 && <p className="text-sm text-[#697585]">Aucune donnée sur la période.</p>}
    {rows.map((r) => {
      const pct = total ? Math.round((r.count / total) * 100) : 0;
      return (
        <div key={r.label}>
          <div className="flex items-baseline justify-between gap-4 text-sm">
            <span className="text-[#203240]">{(labels && labels[r.label]) || r.label}</span>
            <span className="text-[#697585] tabular-nums">
              {r.count} · {pct} %
            </span>
          </div>
          <div className="mt-1 h-1.5 bg-[#F6F0E7] border border-[#D9D3C5]">
            <div className="h-full bg-[#B8956A]" style={{ width: `${pct}%` }} />
          </div>
        </div>
      );
    })}
  </div>
);

/** Where the quote requests come from: page, event type, declared origin, traffic channel. */
export default function EventSourcesPanel() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/admin/events/stats?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const total = data?.total || 0;

  return (
    <section data-testid="event-sources-panel" className="mt-14 border border-[#D9D3C5] bg-[#FFFFFF] p-6 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline">Provenance des demandes de devis</p>
          <h2 className="font-display text-2xl md:text-3xl mt-2 text-[#203240]">
            Quelle page génère le plus de devis ?
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              data-testid={`stats-period-${p.days}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.14em] border transition-colors ${
                days === p.days
                  ? "border-[#B8956A] text-[#B8956A]"
                  : "border-[#D9D3C5] text-[#697585] hover:border-[#B8956A]/60"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="mt-8 text-sm text-[#697585]">Chargement…</p>
      ) : !data ? (
        <p className="mt-8 text-sm text-red-500">Statistiques indisponibles.</p>
      ) : (
        <>
          <div className="mt-8 flex flex-wrap gap-10">
            <div>
              <p className="font-display text-4xl text-[#203240]" data-testid="stats-total">{total}</p>
              <p className="text-xs uppercase tracking-widest text-[#697585] mt-1">Demandes</p>
            </div>
            <div>
              <p className="font-display text-4xl text-[#B8956A]">{data.avg_guests}</p>
              <p className="text-xs uppercase tracking-widest text-[#697585] mt-1">Invités en moyenne</p>
            </div>
            <div className="flex items-end gap-2 text-[#697585]">
              <TrendingUp size={18} className="text-[#B8956A]" />
              <span className="text-sm">
                {data.by_page[0]
                  ? `Meilleure page : ${PAGE_LABELS[data.by_page[0].label] || data.by_page[0].label}`
                  : "—"}
              </span>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <p className="overline mb-4">Par page du site</p>
              <Bars rows={data.by_page} total={total} labels={PAGE_LABELS} testid="stats-by-page" />
            </div>
            <div>
              <p className="overline mb-4">Par type d&apos;événement</p>
              <Bars rows={data.by_type} total={total} testid="stats-by-type" />
            </div>
            <div>
              <p className="overline mb-4">« Comment nous avez-vous connu ? »</p>
              <Bars rows={data.by_origin} total={total} testid="stats-by-origin" />
            </div>
            <div>
              <p className="overline mb-4">Canal d&apos;acquisition (UTM / référent)</p>
              <Bars rows={data.by_channel} total={total} testid="stats-by-channel" />
            </div>
          </div>
        </>
      )}
    </section>
  );
}
